import React, { useEffect } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { graphql } from 'relay-runtime';
import { toGlobalID } from 'src/shared/helpers';
import usePreview3DStore from '../store';
import { loadProductWithCodeQuery } from './__generated__/loadProductWithCodeQuery.graphql';
import { loadProductWithProductQuery } from './__generated__/loadProductWithProductQuery.graphql';

const LoadProductWithId = ({ productId, children }: { productId: string; children: React.ReactNode }) => {
    const [saveVersion, zkProduct, setZkProduct, setIsDemoProduct] = usePreview3DStore((store) => [
        store.saveVersion,
        store.zkProduct,
        store.setZkProduct,
        store.setIsDemoProduct
    ]);
    const initializedLoadRef = React.useRef<{ productId: string; saveVersion: number } | null>(null);

    const data = useLazyLoadQuery<loadProductWithProductQuery>(
        graphql`
            query loadProductWithProductQuery($productId: ID!) {
                model(id: $productId) {
                    id
                    modelID
                    isDemo
                    statusID
                    name
                    code
                    providerCode
                    salesChannelID
                    sourceProductTemplateID
                    defaultDesignTemplateID
                    salesChannel {
                        ecommerceSettings {
                            ecommerceName
                        }
                    }
                    additionalCodes {
                        code
                        salesChannelID
                        merchantProductName
                        salesChannel {
                            ecommerceSettings {
                                ecommerceName
                                remoteUrl
                            }
                        }
                    }

                    modelColorMappings {
                        colorID
                        merchantCode
                        salesChannelID
                    }

                    pricingModelID
                    largeImageUrl
                    smallImageUrl
                    provider
                    environment
                    options {
                        optionID
                        colorID
                        sideID
                        optionsData {
                            isResizable
                            is3DAutoGenEnabled
                            isSplitOutput
                            startWidth
                            minWidth
                            maxWidth
                            stepWidth
                            startHeight
                            minHeight
                            maxHeight
                            stepHeight
                            depth
                            keepAspectRatio
                            lockWidth
                            lockHeight
                            showWidthSize
                            showHeightSize
                            showSplitGrid
                            mirrorType
                            edgesColor
                            minSplitWidth
                            minSplitHeight
                            startSplitColumns
                            startSplitRows
                            presets {
                                name
                                width
                                height
                            }
                        }
                    }
                    printTypes {
                        printTypeID
                        name
                        dpi
                        patID
                        outputTypeID
                        patID
                        ...modelPrintTypeRestrictionsFragment @relay(mask: false)

                        printTypeRestrictionsID
                        printTypeRestrictions {
                            ...partPrintTypeRestrictionsFragment @relay(mask: false)
                        }
                    }
                    colors {
                        colorID
                        colorGuid
                        name
                        code
                        colorProviderCode
                        position
                        sides {
                            sideID
                            sideGuid
                            name
                            tempImageFileKey
                            imageUrl
                            ppcm
                            code
                            sortOrder
                            mandatory
                            canRotate
                            cameraLocationID
                            areas {
                                areaID
                                boundsWidth
                                boundsHeight
                                boundsX
                                boundsY
                                path
                                pathSVG
                                name
                                clipOut
                                guid
                                layers {
                                    layerID
                                    name
                                    displayName
                                    description
                                    displayOrder
                                    isVisibleInCanvas
                                    isVisibleInCart
                                    isVisibleInOutput
                                    showLabel
                                    isClipMask
                                    isOutputClipped
                                    showIn3DPreview
                                    svg
                                    json
                                }
                                templateFile {
                                    fileID
                                    fileObjectID
                                    documentWidth
                                    documentHeight
                                }
                            }
                        }
                    }
                    scenes {
                        sceneID
                        isDefault
                        meshDesignEffects {
                            effectID
                            meshID
                            subMeshID
                            name
                            materialID
                            isDefault
                            config {
                                general {
                                    showDesign
                                    desktopTextureResolutionWidth
                                    desktopTextureResolutionHeight
                                    mobileTextureResolutionWidth
                                    mobileTextureResolutionHeight
                                }
                                diffuse {
                                    enabled
                                    blendMode
                                    level
                                }
                                opacity {
                                    enabled
                                }
                                normalmap {
                                    enabled
                                    showDesign
                                    showOnDesign
                                    showOnMesh
                                    bias
                                    invertR
                                    invertG
                                    preBlurEnabled
                                    preBlur
                                    postBlurEnabled
                                    postBlur
                                    intensity
                                    bevel
                                }
                                outerShadow {
                                    enabled
                                    color
                                    angle
                                    distance
                                    size
                                }
                                innerShadow {
                                    enabled
                                    color
                                    angle
                                    distance
                                    size
                                }
                            }
                        }
                        meshSides {
                            key
                            meshID
                            subMeshID
                            sideID
                            colorID
                            attributeID
                            matrix
                            materialID
                            materialTexture
                            isNormalmapEnabled
                            percWidth
                            percHeight
                            uvChannel
                            config {
                                general {
                                    showDesign
                                    desktopTextureResolutionWidth
                                    desktopTextureResolutionHeight
                                    mobileTextureResolutionWidth
                                    mobileTextureResolutionHeight
                                }
                                diffuse {
                                    enabled
                                    blendMode
                                    level
                                }
                                opacity {
                                    enabled
                                }
                                normalmap {
                                    enabled
                                    showDesign
                                    showOnDesign
                                    showOnMesh
                                    bias
                                    invertR
                                    invertG
                                    preBlurEnabled
                                    preBlur
                                    postBlurEnabled
                                    postBlur
                                    intensity
                                    bevel
                                }
                                outerShadow {
                                    enabled
                                    color
                                    angle
                                    distance
                                    size
                                }
                                innerShadow {
                                    enabled
                                    color
                                    angle
                                    distance
                                    size
                                }
                            }
                            transform {
                                positionX
                                positionY
                                scalingX
                                scalingY
                                rotation
                            }
                        }
                    }
                    preview3D {
                        variantsAttributeOptions {
                            attributeGuid
                            variableMaterials {
                                materialID
                                meshes
                            }
                            variantOptions {
                                variantID
                                optionGuid
                                meshes
                                materials {
                                    optionMaterialID
                                    variableMaterialID
                                    actions {
                                        actionGuid
                                        meshID
                                    }
                                }
                            }
                        }
                    }
                    attributes {
                        attributeID
                        attributeGuid
                        attributeCode
                        attributeTypeID
                        defaultValue
                        displayOrder
                        enabled
                        name
                        description
                        groupID
                        groupGuid
                        stepID
                        stepGuid
                        optionPreviewShapeID
                        meshSelector
                        sceneCameraLocationID
                        hideOptionsLabel
                        constraintExpression {
                            propositions {
                                attributeGuid
                                op
                                value
                            }
                            op
                            expressions {
                                op
                                propositions {
                                    attributeGuid
                                    op
                                    value
                                }
                            }
                        }
                        pricings {
                            attributePricingID
                            selectedOptionID
                            selectedOptionGuid
                            selectedValue
                            price
                            pricingID
                            name
                            constraintExpression {
                                op
                                propositions {
                                    attributeGuid
                                    op
                                    value
                                }
                                expressions {
                                    op
                                    propositions {
                                        attributeGuid
                                        op
                                        value
                                    }
                                }
                            }
                        }
                        preActions {
                            actionID
                            actionGuid
                            name
                            actionTypeID
                            sceneElementTypeID
                            selector
                            propertyName
                            value
                            execOrder
                            enabled
                            deviceTypes
                        }
                        valueActions {
                            actionID
                            actionGuid
                            name
                            actionTypeID
                            sceneElementTypeID
                            selector
                            propertyName
                            value
                            execOrder
                            enabled
                            deviceTypes
                        }
                        postActions {
                            actionID
                            actionGuid
                            name
                            actionTypeID
                            sceneElementTypeID
                            selector
                            propertyName
                            value
                            execOrder
                            enabled
                            deviceTypes
                        }
                        options {
                            optionID
                            optionGuid
                            optionCode
                            tempImageFileKey
                            imageUrl
                            tempImageFileObjectID
                            imageFileObjectID
                            name
                            description
                            displayOrder
                            enabled
                            isDefault
                            constraintExpression {
                                op
                                propositions {
                                    attributeGuid
                                    op
                                    value
                                }
                                expressions {
                                    op
                                    propositions {
                                        attributeGuid
                                        op
                                        value
                                    }
                                }
                            }
                            actions {
                                actionID
                                actionGuid
                                name
                                actionTypeID
                                sceneElementTypeID
                                selector
                                propertyName
                                value
                                execOrder
                                enabled
                                deviceTypes
                            }
                            extensionFields {
                                extensionFieldID
                                value
                                name
                                label
                                formatString
                                dataTypeID
                                operationTypeID
                            }
                        }
                    }
                    attributeSteps {
                        stepID
                        stepGuid
                        groupID
                        groupGuid
                        name
                        displayOrder
                        enabled
                        cameraLocationID
                    }
                    templateGroups {
                        templateGroupID
                        templateGroupGuid
                        name
                        displayOrder
                        groupID
                        groupGuid
                        stepID
                        stepGuid
                        compositions
                        macrocategories
                        categories {
                            categoryID
                            macroCategoryID
                        }
                    }
                    attributeGroups {
                        groupID
                        groupGuid
                        tempImageFileKey
                        tempImageUrl
                        imageUrl
                        name
                        displayOrder
                        enabled
                        sceneCameraLocationID
                        meshSelector
                        direction
                        attributesAlwaysOpened
                    }
                }
            }
        `,
        {
            productId: productId
        },
        { fetchPolicy: 'network-only', fetchKey: saveVersion }
    );

    useEffect(() => {
        // Relay merges product-only save responses into this query. Those partial
        // updates must not replace the writable model while Composer is reconciling
        // assigned side/item IDs. Explicit Scene Editor saves increment saveVersion;
        // navigating to another product also starts a new load.
        if (
            initializedLoadRef.current?.productId === productId &&
            initializedLoadRef.current.saveVersion === saveVersion &&
            zkProduct?.get('modelID') === data.model?.modelID
        )
            return;
        // Clone the data to be not read only because we need to merge the model when closing the dialogs
        const dataClone = JSON.parse(JSON.stringify(data.model));
        const newZkProduct = new MPlaza.Model(dataClone);
        setIsDemoProduct(data.model?.isDemo === true);

        // For DAM products there are no variants, but they are required for the code
        if (newZkProduct.get('colors').length === 0) {
            const newModelColor = new MPlaza.Color();
            newModelColor.set('colorID', 1);
            newZkProduct.get('colors').add(newModelColor);
        }

        setZkProduct(newZkProduct);
        initializedLoadRef.current = { productId, saveVersion };
    }, [data.model, productId, saveVersion, setIsDemoProduct, setZkProduct, zkProduct]);

    // Wait for the product to be loaded
    if (!zkProduct) return null;

    return children;
};

export const LoadProductWithCode = ({ productCode, children }: { productCode: string; children: React.ReactNode }) => {
    const query = useLazyLoadQuery<loadProductWithCodeQuery>(
        graphql`
            query loadProductWithCodeQuery($productCode: String!, $salesChannelId: String!) {
                modelIDByCode(code: $productCode, salesChannelId: $salesChannelId)
            }
        `,
        {
            productCode,
            salesChannelId: toGlobalID(1, 'SalesChannel')
        }
    );

    return <LoadProductWithId productId={query.modelIDByCode}>{children}</LoadProductWithId>;
};

export const LoadProduct = ({ productId, productCode, children }: { productId?: string | null; productCode?: string | null; children: React.ReactNode }) => {
    return productCode ? (
        <LoadProductWithCode productCode={productCode}>{children}</LoadProductWithCode>
    ) : (
        <LoadProductWithId productId={productId!}>{children}</LoadProductWithId>
    );
};
